import React, { useState, useMemo, useCallback } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Button, { LoadingButton } from '@atlaskit/button';
import {
  useFormSubmit,
  useNotification,
  ErrorComponents,
  InfoPanel,
  useSidebar,
  successNotificationBuilder,
  NotificationTypes,
  getTaxIds,
  getFallbackTaxId,
} from '@css-xp/base';
import { CommerceAddress, PaymentMethod, Maybe } from '@css-xp/graphtypes';
import ButtonGroup from '@atlaskit/button/button-group';
import { createInvoiceGroup } from '@atlassian/temp-proxies';
import { CreateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import { withApollo } from '@css-xp/base/graphql-provider';
import defaultTo from 'lodash/defaultTo';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';
import './create-invoice-group-sidebar.scss';
import {
  SoldToAddressStep,
  InvoiceContactsStep,
  CurrencyPONumberStep,
  PaymentMethodStep,
  FormData,
} from '../../../invoice-group/src/components/billing-profile-components';
import '../../../invoice-group/src/components/billing-profile-components/create-billing-profile.scss';

enum FormStages {
  SOLD_TO_ADDRESS_STEP = 'Sold-to address',
  INVOICE_CONTACTS_STEP = 'Additional invoice recipients',
  PAYMENT_METHOD_STEP = 'Payment method',
  CURRENCY_AND_PO_NUMBER_STEP = 'Currency & PO number',
}

enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

enum FormTrackerStep {
  SOLD_TO_ADDRESS_STEP = 0,
  INVOICE_CONTACTS_STEP,
  PAYMENT_METHOD_STEP,
  CURRENCY_AND_PO_NUMBER_STEP,
}

const stages = Object.values(FormStages);

const isCompleteSoldToFormEmpty = (billingProfileData: FormData) => {
  const { soldToName, soldToAddress1, soldToCountry, soldToState, soldToCity, soldToPostalCode } = billingProfileData;
  if (
    !soldToName &&
    !soldToAddress1 &&
    !soldToCountry?.value &&
    !soldToState?.value &&
    !soldToCity &&
    !soldToPostalCode
  ) {
    return true;
  }
  return false;
};

export const isValidAddress = (billingAddress: FormData): boolean => {
  const { soldToName, soldToAddress1, soldToCountry, soldToState, soldToCity, soldToPostalCode } = billingAddress;
  if (
    (soldToName || soldToAddress1 || soldToCountry?.value || soldToState?.value || soldToCity || soldToPostalCode) &&
    !(soldToName && soldToAddress1 && soldToCountry?.value && soldToState?.value && soldToCity && soldToPostalCode)
  ) {
    return false;
  }
  return true;
};

export function getError(billingProfileData: FormData) {
  const { soldToName, soldToAddress1, soldToCountry, soldToState, soldToCity, soldToPostalCode } = billingProfileData;
  const missingFields: string[] = [];
  if (!soldToName) missingFields.push('Company Name');
  if (!soldToAddress1) missingFields.push('Address 1');
  if (!soldToCountry?.value) missingFields.push('Country');
  if (!soldToState?.value) missingFields.push('State');
  if (!soldToCity) missingFields.push('City');
  if (!soldToPostalCode) missingFields.push('Postal Code');

  const errorMsg = missingFields.length
    ? missingFields.join(', ') + `${missingFields.length > 1 ? ' are' : ' is'} missing`
    : '';
  return errorMsg;
}

export const getShipToAddress = (finalFormData: FormData) => {
  const taxIds = getTaxIds(finalFormData);
  let shipTo: CreateInvoiceGroupRequest['data']['shipToParty'] | null;
  if (isCompleteSoldToFormEmpty(finalFormData)) {
    shipTo = null;
  } else {
    shipTo = {
      name: defaultTo(finalFormData.soldToName, ''),
      postalAddress: {
        line1: finalFormData.soldToAddress1,
        line2: finalFormData.soldToAddress2 === '' ? null : finalFormData.soldToAddress2,
        city: finalFormData.soldToCity,
        country: finalFormData.soldToCountry?.value,
        state: finalFormData.soldToState?.value,
        postcode: finalFormData.soldToPostalCode,
      },
    };
    shipTo.taxId = finalFormData?.soldToTaxId || getFallbackTaxId(taxIds);
    shipTo.taxIds = taxIds.length ? taxIds : null;
  }
  return shipTo;
};

export const formatShipToAddress = (shipToParty: CommerceAddress | undefined | null) => {
  if (!shipToParty?.taxIds) return shipToParty;
  const formattedTaxIds = shipToParty.taxIds.map((each) => ({
    id: each?.id,
    label: each?.label,
    vatStatus: each?.metadata?.vatStatus || null,
  }));
  return {
    ...shipToParty,
    taxId: shipToParty?.taxId || getFallbackTaxId(shipToParty.taxIds),
    taxIds: formattedTaxIds,
  };
};

export const CreateInvoiceGroupSidebar = withApollo(
  ({
    transactionAccountId,
    billToAddress,
    refetch,
    availablePaymentMethods,
    updatePaymentMethodLink,
    termPaymentStatus,
  }: {
    transactionAccountId: string;
    billToAddress: CommerceAddress;
    refetch?: () => Promise<ApolloQueryResult<unknown>>;
    availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
    updatePaymentMethodLink?: Maybe<string>;
    termPaymentStatus?: Maybe<string> | null;
  }) => {
    const [isPOMultipleUseChecked, setIsPOMultipleUseChecked] = useState(false);
    const [shipToAddress, setShipToAddress] = useState<CommerceAddress | undefined | null>({});
    const [currentProgressIndex, setCurrentProgressIndex] = useState<number>(0);
    const [billingProfileData, setBillingProfileData] = useState<FormData>({});
    const [isNotValidAddress, setIsNotValidAddress] = useState<boolean>(false);

    const { closeSidebar } = useSidebar();

    const isPONumberFeatureEnabled = true;

    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

    const { error, isProcessing, submitForm, complete, data } = useFormSubmit<CreateInvoiceGroupRequest, any>({
      submitFormAction: createInvoiceGroup,
    });

    const submitCreateInvoiceGroupForm = useCallback(
      (formData: FormData) => {
        setIsNotValidAddress(false);
        const finalFormData = { ...billingProfileData, ...formData };
        setBillingProfileData(finalFormData);
        if (currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP && !isValidAddress(finalFormData)) {
          setIsNotValidAddress(true);
          return;
        }
        if (currentProgressIndex < stages.length - 1) {
          setCurrentProgressIndex(currentProgressIndex + 1);
          return;
        }

        const invoiceContacts = [];
        for (const key in finalFormData) {
          // REMOVAL OF (-) FROM RECIPIENTS
          if (key.includes('recipients')) {
            // @ts-ignore
            invoiceContacts.push(finalFormData[key]);
          }
        }

        const requestForm: CreateInvoiceGroupRequest = {
          transactionAccountId: transactionAccountId,
          data: {
            name: defaultTo(finalFormData.invoiceGroupName, ''),
            shipToParty: getShipToAddress(finalFormData),
            currency: defaultTo(finalFormData.currency, ''),
            recipients: invoiceContacts.filter((recipient: string) => recipient.length > 0),
          },
        };

        if (finalFormData.defaultPaymentMethod) {
          requestForm.data = {
            ...requestForm.data,
            defaultPaymentMethod: finalFormData.defaultPaymentMethod,
          };
        }

        const existingShipTo = {
          shipToParty: {
            name: shipToAddress?.name,
            taxId: shipToAddress?.taxId || null,
            taxIds: shipToAddress?.taxIds?.length ? shipToAddress.taxIds : null,
            postalAddress: {
              line1: shipToAddress?.addressLine1,
              line2: shipToAddress?.addressLine2,
              city: shipToAddress?.city,
              country: shipToAddress?.country,
              state: shipToAddress?.state,
              postcode: shipToAddress?.postcode,
            },
          },
        };

        // check if we selected an existing shipTo and not made any updates to it
        if (
          !isEmpty(shipToAddress) &&
          isEqual(formatShipToAddress(existingShipTo.shipToParty), formatShipToAddress(requestForm.data.shipToParty))
        ) {
          // send existing shipToPartyId to update it
          requestForm.data.shipToPartyId = shipToAddress?.id;
        }
        if (isCompleteSoldToFormEmpty(finalFormData)) {
          requestForm.data.shipToPartyId = billToAddress?.id;
        }

        if (isPONumberFeatureEnabled && finalFormData.purchaseOrderNumber) {
          requestForm.data.purchaseOrder = {
            number: finalFormData.purchaseOrderNumber,
            oneTimeUse: !isPOMultipleUseChecked,
          };
        }

        submitForm(requestForm);
      },
      [
        billingProfileData,
        currentProgressIndex,
        isPONumberFeatureEnabled,
        shipToAddress,
        submitForm,
        transactionAccountId,
        isPOMultipleUseChecked,
      ]
    );

    if (complete && !notificationSent) {
      const successMessage = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('create-invoice-group-success')
        .withHeader(`Created billing profile ${data?.name}.`)
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(successMessage);
      setNotificationSent(true);
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.CREATED,
        actionSubject: AnalyticsTrackSubject.BILLING_PROFILE,
        attributes: {
          profileName: data?.name,
        },
      });
      closeSidebar();
      if (refetch) {
        refetch();
      }
    }

    const onPOMultipleUseChangeEvent = () => {
      setIsPOMultipleUseChecked((current) => !current);
    };

    const getFormStep = useCallback(
      (formProps: {
        disabled: boolean;
        getValues: () => FormData;
        setFieldValue: (name: string, value: any) => void;
      }) => {
        switch (currentProgressIndex) {
          case FormTrackerStep.SOLD_TO_ADDRESS_STEP: {
            return (
              <SoldToAddressStep
                onFieldValueChange={formProps.setFieldValue}
                transactionAccountId={transactionAccountId}
                billToAddress={billToAddress}
                shipToAddress={shipToAddress}
                onShipToAddressUpdate={setShipToAddress}
                billingProfileData={billingProfileData}
                page="transaction-account"
              />
            );
          }
          case FormTrackerStep.INVOICE_CONTACTS_STEP: {
            const invoiceContacts = [];
            for (const key in billingProfileData) {
              if (key.includes('recipients')) {
                // @ts-ignore
                invoiceContacts.push(billingProfileData[key]);
              }
            }
            return (
              <InvoiceContactsStep
                onUpdateAdminFieldsDirtyFlag={() => true}
                defaultRecipients={invoiceContacts}
                namePrefix="recipients-"
              />
            );
          }
          case FormTrackerStep.PAYMENT_METHOD_STEP: {
            return (
              <PaymentMethodStep
                availablePaymentMethods={availablePaymentMethods}
                updatePaymentMethodLink={updatePaymentMethodLink}
                onFieldValueChange={formProps.setFieldValue}
                selectedPaymentId={billingProfileData?.defaultPaymentMethod}
                termPaymentStatus={termPaymentStatus}
              />
            );
          }
          default:
            return (
              <CurrencyPONumberStep
                isPONumberFeatureEnabled={isPONumberFeatureEnabled}
                isPOMultipleUseChecked={isPOMultipleUseChecked}
                onPOMultipleUseChangeEvent={onPOMultipleUseChangeEvent}
                purchaseOrderNumber={billingProfileData?.purchaseOrderNumber ?? ''}
              />
            );
        }
      },
      [
        currentProgressIndex,
        transactionAccountId,
        billToAddress,
        isPONumberFeatureEnabled,
        isPOMultipleUseChecked,
        shipToAddress,
        billingProfileData,
        availablePaymentMethods,
        updatePaymentMethodLink,
        termPaymentStatus,
      ]
    );

    const items: Stages = useMemo(() => {
      return stages.map((stage, index) => ({
        id: `${index}`,
        label: stage,
        percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
        status:
          currentProgressIndex > index
            ? TrackerState.VISITED
            : currentProgressIndex === index
            ? TrackerState.CURRENT
            : TrackerState.UNVISITED,
        onClick: () => setCurrentProgressIndex(index),
      }));
    }, [currentProgressIndex]);

    const skipOrCancelForm = () => {
      switch (currentProgressIndex) {
        case FormTrackerStep.INVOICE_CONTACTS_STEP:
          setCurrentProgressIndex(currentProgressIndex + 1);
          break;
        default:
          closeSidebar();
          break;
      }
    };

    return (
      <div className="billing-profile">
        <Form onSubmit={submitCreateInvoiceGroupForm}>
          {({ formProps, submitting, dirty, ...props }) => (
            <form {...formProps} data-testid="create-billing-profile-form">
              <div className="header">
                <FormHeader title="Create new billing profile"></FormHeader>
              </div>
              <div className="progress-content progress-parent-div">
                <ProgressTracker items={items} spacing="compact" />
              </div>
              <FormSection>
                {currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP ? <h3>Billing profile name</h3> : null}

                {error && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to create a new invoice group for the following reason"
                        message={error}
                      />
                    }
                  />
                )}
                {isNotValidAddress && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to create a new invoice group for the following reason"
                        message={getError(billingProfileData)}
                      />
                    }
                  />
                )}
              </FormSection>
              {getFormStep(props)}
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="default" onClick={skipOrCancelForm} testId="cancel">
                    {currentProgressIndex === FormTrackerStep.INVOICE_CONTACTS_STEP ? 'Skip' : 'Cancel'}
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isDisabled={currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP && !dirty}
                    isLoading={submitting || isProcessing}
                    testId={submitting ? 'loading-button' : 'create-button'}
                  >
                    {currentProgressIndex === stages.length - 1 ? 'Create' : 'Next'}
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            </form>
          )}
        </Form>
      </div>
    );
  }
);
